// Brief "look here" spotlight, borrowing the onboarding tour's visual language.
//
// The tour's own spotlight is welded to its step machine (tooltip placement,
// step transitions, the dim backdrop), so this is the standalone version: no
// tooltip, no dim, no state. You point it at an element and it draws the same
// amber ring for a couple of seconds, then gets out of the way.
//
// Used by the Raid Ready checker. Clicking a row scrolls you somewhere, and
// landing on a long page with no idea which section just moved is useless, so
// the destination announces itself.
//
// Order matters: travel first, ring second. Drawing the ring at click time
// means it spends the whole smooth scroll a frame behind its target while the
// destination tab is still mounting, which looks like the gold box is losing a
// fight with the page. So it waits for the scroll to settle and only then
// fades in, already sitting on the section.
//
// Like the tour, it never traps anyone: the overlay is pointer-events:none and
// lives outside the React tree, so the page stays fully clickable throughout.

const STYLE_ID = "lfm-spotlight-styles";
const OVERLAY_ID = "lfm-spotlight";

// Same amber the tour pulses with, so the two read as one mechanism.
const RING =
  "0 0 0 2px rgba(245,158,11,1), 0 0 0 7px rgba(245,158,11,0.22), 0 0 30px 8px rgba(245,158,11,0.38)";
const RING_WIDE =
  "0 0 0 3px rgba(245,158,11,1), 0 0 0 12px rgba(245,158,11,0.12), 0 0 42px 13px rgba(245,158,11,0.52)";

export interface FlashOptions {
  /** How long the ring stays up, in ms. */
  duration?: number;
  /** Corner radius. Match the tour step's radius for the same target. */
  radius?: number;
  /** Breathing room between the element's edge and the ring. */
  padding?: number;
  /** How long to keep looking for a selector that hasn't rendered yet. */
  waitMs?: number;
  /** Scroll the target into view and wait for it to land before ringing it. */
  scroll?: boolean;
  /** Where the target lands when scrolled. Defaults to the top, as callers did. */
  block?: ScrollLogicalPosition;
  /**
   * For targets hidden until their parent is hovered (the sync button's card
   * column), force the parent open for the duration. Mirrors the tour's flag.
   */
  revealParent?: boolean;
}

let active: (() => void) | null = null;
// Bumped by every call. A flash that is still waiting on its target or on the
// scroll compares against this before drawing, so a superseded call never
// arrives late and paints a ring nobody asked for.
let generation = 0;

function ensureStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes lfmSpotIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes lfmSpotOut { from { opacity: 1; } to { opacity: 0; } }
    @keyframes lfmSpotPulse {
      0%,100% { box-shadow: ${RING}; }
      50%     { box-shadow: ${RING_WIDE}; }
    }
  `;
  document.head.appendChild(style);
}

function resolve(target: string | Element, waitMs: number): Promise<Element | null> {
  if (typeof target !== "string") return Promise.resolve(target);
  const found = document.querySelector(target);
  if (found) return Promise.resolve(found);
  // The caller has usually just switched tabs, so the node is a few frames out.
  return new Promise((done) => {
    const deadline = Date.now() + waitMs;
    const tick = () => {
      const el = document.querySelector(target);
      if (el) return done(el);
      if (Date.now() > deadline) return done(null);
      window.setTimeout(tick, 80);
    };
    window.setTimeout(tick, 80);
  });
}

/**
 * Wait until the target has stopped moving in the viewport.
 *
 * A smooth scroll keeps the element travelling for several hundred ms, and the
 * home page is still mounting the section it just switched to, so a ring drawn
 * at call time spends that whole trip a frame behind the thing it is meant to
 * be pointing at. Watching the element's own rect covers both causes at once
 * and works inside nested scroll containers, which `scrollend` does not do
 * reliably.
 *
 * Resolves early if the scroll never starts (the target was already parked
 * where it belongs), and refuses to wait forever on a page that never settles,
 * e.g. an animating banner.
 */
function settle(el: Element, graceMs = 140, maxMs = 1200): Promise<void> {
  const r = el.getBoundingClientRect();
  // Nothing measurable to wait on: no layout engine (jsdom) or a zero-size
  // anchor. Draw immediately rather than sitting out the grace period.
  if (!r.width && !r.height) return Promise.resolve();

  return new Promise((done) => {
    const started = Date.now();
    let last = r.top;
    let moved = false;
    let stable = 0;
    const tick = () => {
      const top = el.getBoundingClientRect().top;
      if (Math.abs(top - last) > 0.5) {
        moved = true;
        stable = 0;
      } else {
        stable += 1;
      }
      last = top;
      const elapsed = Date.now() - started;
      if (moved && stable >= 3) return done();
      if (!moved && elapsed > graceMs) return done();
      if (elapsed > maxMs) return done();
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

/**
 * Flash the tour's ring around an element. Safe to call with a selector for
 * something that hasn't mounted yet, and safe to call twice in a row: the
 * second call replaces the first rather than stacking rings.
 */
export function flashSpotlight(target: string | Element, opts: FlashOptions = {}) {
  const {
    duration = 2000,
    radius = 16,
    padding = 6,
    waitMs = 3000,
    scroll = true,
    block = "start",
    revealParent = false,
  } = opts;

  if (typeof document === "undefined") return;
  // The onboarding tour owns the screen while it runs; two amber rings at once
  // would just be noise.
  try {
    if ((window as any).__lfmTourActive) return;
  } catch {}

  const mine = ++generation;
  active?.();

  (async () => {
    const el = await resolve(target, waitMs);
    if (!el || generation !== mine) return;

    ensureStyles();

    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    // Reveal before scrolling: a parent that unfolds shifts everything below it,
    // and we want the scroll aiming at the final layout.
    let restoreParent: (() => void) | null = null;
    if (revealParent && el.parentElement) {
      const parent = el.parentElement as HTMLElement;
      const prevWidth = parent.style.width;
      const prevOpacity = parent.style.opacity;
      parent.style.width = "auto";
      parent.style.opacity = "1";
      restoreParent = () => {
        parent.style.width = prevWidth;
        parent.style.opacity = prevOpacity;
      };
    }

    // Travel first, then announce. The ring is the payoff for the click, so it
    // should land on a page that has already stopped moving rather than skid
    // across one that hasn't.
    if (scroll) {
      el.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block });
      await settle(el);
      if (generation !== mine) return restoreParent?.();
    }

    const node = document.createElement("div");
    node.id = OVERLAY_ID;
    node.setAttribute("aria-hidden", "true");
    node.style.cssText = [
      "position:fixed",
      "pointer-events:none",
      // Just under the tour's own layer, so the tour always wins.
      "z-index:9989",
      `border-radius:${radius}px`,
      `box-shadow:${RING}`,
      reduced ? "" : "animation:lfmSpotIn 0.18s ease-out, lfmSpotPulse 1.6s ease-in-out 0.18s infinite",
    ]
      .filter(Boolean)
      .join(";");

    // Geometry before the DOM sees it: appending an unpositioned ring gives it
    // one frame as a dot in the top-left corner, and the fade-in starts on that
    // frame, so it reads as the ring flying in from nowhere.
    let raf = 0;
    const place = () => {
      const r = el.getBoundingClientRect();
      node.style.top = `${r.top - padding}px`;
      node.style.left = `${r.left - padding}px`;
      node.style.width = `${r.width + padding * 2}px`;
      node.style.height = `${r.height + padding * 2}px`;
    };
    place();
    document.body.appendChild(node);

    // The target can still move under us afterwards: the user scrolls, or a
    // late image reflows the section. Re-read every frame so the ring stays
    // welded to it instead of orbiting empty space.
    const track = () => {
      place();
      raf = requestAnimationFrame(track);
    };
    raf = requestAnimationFrame(track);

    let fadeTimer = 0;
    let endTimer = 0;

    const cleanup = () => {
      if (active === cleanup) active = null;
      cancelAnimationFrame(raf);
      window.clearTimeout(fadeTimer);
      window.clearTimeout(endTimer);
      restoreParent?.();
      node.remove();
    };
    active = cleanup;

    fadeTimer = window.setTimeout(() => {
      node.style.animation = reduced ? "" : "lfmSpotOut 0.32s ease-in forwards";
      if (reduced) node.style.opacity = "0";
      endTimer = window.setTimeout(cleanup, 340);
    }, duration);
  })();
}

/** Corner radii matching the onboarding tour's steps for the same anchors. */
export const SPOTLIGHT_RADII: Record<string, number> = {
  availability: 16,
  "join-guild": 14,
  squad: 16,
};
